import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CircleParking } from "lucide-react";

import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import type { PropertyType } from "@/lib/mock-data";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  SERVICE_LABEL,
  SERVICE_LISTING_TYPES,
  useMyServices,
  type ServiceType,
} from "@/lib/services";

export const Route = createFileRoute("/_authenticated/partner/properties/new")({
  head: () => ({
    meta: [
      { title: "Add listing · Landoo" },
      {
        name: "description",
        content: "Create a Sale, Rent or Lease listing under one of your active Landoo services.",
      },
    ],
  }),
  component: NewPropertyPage,
});

interface PropertyTypeRow {
  id: string;
  slug: PropertyType;
  label: string;
}

const PROPERTY_SERVICES: ServiceType[] = ["sale", "rent", "lease"];

function NewPropertyPage() {
  const { user } = useAuth();
  const { canList, activeServices, isLoading: svcLoading } = useMyServices();
  const nav = useNavigate();
  const [types, setTypes] = useState<PropertyTypeRow[]>([]);
  const available = PROPERTY_SERVICES.filter((s) => canList(s));
  const [service, setService] = useState<ServiceType | "">("");
  const [form, setForm] = useState({
    title: "",
    description: "",
    listing_type: "",
    type_id: "",
    price: "",
    bedrooms: "",
    bathrooms: "",
    area_sqft: "",
    address: "",
    city: "",
    latitude: "",
    longitude: "",
    image_url: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    supabase
      .from("property_types")
      .select("id,slug,label")
      .order("sort_order")
      .then(({ data }) => {
        const rows = (data as PropertyTypeRow[]) ?? [];
        setTypes(rows);
        setForm((f) => ({ ...f, type_id: f.type_id || rows[0]?.id || "" }));
      });
  }, []);

  useEffect(() => {
    if (!service && available.length) {
      setService(available[0]);
      setForm((f) => ({ ...f, listing_type: SERVICE_LISTING_TYPES[available[0]][0] }));
    }
  }, [available.join(",")]);

  const submit = async (status: "draft" | "pending") => {
    if (!user || !service) return;
    if (!canList(service))
      return toast.error(`${SERVICE_LABEL[service]} service is not active on your account`);
    if (!form.title || !form.type_id) return toast.error("Title and type are required");
    setSubmitting(true);
    const { error } = await supabase.from("properties").insert({
      owner_id: user.id,
      title: form.title,
      description: form.description || null,
      purpose: service,
      listing_type: form.listing_type || null,
      type_id: form.type_id,
      price: form.price ? Number(form.price) : 0,
      bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
      bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
      area_sqft: form.area_sqft ? Number(form.area_sqft) : null,
      address: form.address || null,
      city: form.city || null,
      latitude: form.latitude ? Number(form.latitude) : null,
      longitude: form.longitude ? Number(form.longitude) : null,
      image_url: form.image_url || null,
      status,
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success(status === "pending" ? "Submitted for review" : "Draft saved");
    nav({ to: "/partner/properties" });
  };

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  if (!svcLoading && available.length === 0) {
    return (
      <DashboardShell title="Add listing" role="Partner" items={PARTNER_NAV}>
        <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          You have no active Sale, Rent or Lease service.
          {activeServices.includes("parking") &&
            " Your Parking service does not allow property listings."}{" "}
          Each service needs its own registration, verification, agreement, subscription and admin
          approval.
          <div className="mt-3">
            <Link
              to="/partner/services"
              className="inline-flex rounded-full bg-brand px-4 py-2 text-xs font-semibold text-brown-dark shadow-soft"
            >
              Go to my services
            </Link>
          </div>
        </div>
      </DashboardShell>
    );
  }

  const isRentOrLease = service === "rent" || service === "lease";

  return (
    <DashboardShell title="Add listing" role="Partner" items={PARTNER_NAV}>
      {canList("parking") && (
        <div className="mx-auto mb-3 flex max-w-2xl flex-wrap items-center justify-between gap-2 rounded-2xl border border-border bg-card p-3 shadow-soft">
          <div className="text-xs text-muted-foreground">
            Parking is a separate service with its own form.
          </div>
          <Link
            to="/partner/parking/new"
            className="inline-flex items-center gap-1.5 rounded-full bg-parking px-3.5 py-2 text-xs font-semibold text-white shadow-soft"
          >
            <CircleParking className="h-4 w-4" /> Add parking place
          </Link>
        </div>
      )}

      <form
        className="mx-auto max-w-2xl space-y-4 rounded-2xl border border-border bg-card p-5 shadow-soft"
        onSubmit={(e) => {
          e.preventDefault();
          submit("pending");
        }}
      >
        <Field label="Service (only your active services)">
          <div className="flex flex-wrap gap-1.5">
            {available.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setService(s);
                  set("listing_type", SERVICE_LISTING_TYPES[s][0]);
                }}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  service === s
                    ? "bg-brown text-primary-foreground"
                    : "bg-secondary text-brown-dark"
                }`}
              >
                {SERVICE_LABEL[s]}
              </button>
            ))}
          </div>
        </Field>

        {service && (
          <div className="rounded-xl bg-brand-soft px-3 py-2 text-[11px] font-semibold text-brown-dark">
            {SERVICE_LABEL[service]} listing — no fields from the other services are shown.
          </div>
        )}

        <Field label="Title">
          <input
            required
            className={inp}
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={`${service ? SERVICE_LABEL[service] : ""} listing type`}>
            <select
              className={inp}
              value={form.listing_type}
              onChange={(e) => set("listing_type", e.target.value)}
            >
              {(service ? SERVICE_LISTING_TYPES[service] : []).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Property category">
            <select
              className={inp}
              value={form.type_id}
              onChange={(e) => set("type_id", e.target.value)}
            >
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Address">
            <input
              className={inp}
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
            />
          </Field>
          <Field label="City">
            <input
              className={inp}
              value={form.city}
              onChange={(e) => set("city", e.target.value)}
            />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Latitude">
            <input
              type="number"
              step="any"
              className={inp}
              value={form.latitude}
              onChange={(e) => set("latitude", e.target.value)}
            />
          </Field>
          <Field label="Longitude">
            <input
              type="number"
              step="any"
              className={inp}
              value={form.longitude}
              onChange={(e) => set("longitude", e.target.value)}
            />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Bedrooms">
            <input
              type="number"
              className={inp}
              value={form.bedrooms}
              onChange={(e) => set("bedrooms", e.target.value)}
            />
          </Field>
          <Field label="Bathrooms">
            <input
              type="number"
              className={inp}
              value={form.bathrooms}
              onChange={(e) => set("bathrooms", e.target.value)}
            />
          </Field>
          <Field label="Area (sqft)">
            <input
              type="number"
              className={inp}
              value={form.area_sqft}
              onChange={(e) => set("area_sqft", e.target.value)}
            />
          </Field>
        </div>
        <Field
          label={
            isRentOrLease
              ? `${SERVICE_LABEL[service as ServiceType]} amount per month (₹)`
              : "Sale price (₹)"
          }
        >
          <input
            type="number"
            className={inp}
            value={form.price}
            onChange={(e) => set("price", e.target.value)}
          />
        </Field>
        <Field label="Image URL">
          <input
            className={inp}
            value={form.image_url}
            onChange={(e) => set("image_url", e.target.value)}
            placeholder="https://…"
          />
        </Field>
        <Field label="Description">
          <textarea
            className={inp}
            rows={4}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </Field>
        <div className="rounded-xl border border-dashed border-border bg-cream/50 p-3 text-xs text-muted-foreground">
          Contact details stay private until you enable sharing. Admins review submissions before
          they go live.
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => submit("draft")}
            disabled={submitting}
            className="flex-1 rounded-xl border border-border bg-background py-2.5 text-sm font-semibold text-brown-dark disabled:opacity-50"
          >
            Save draft
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 rounded-xl bg-coral py-2.5 text-sm font-semibold text-white shadow-soft disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit for review"}
          </button>
        </div>
      </form>
    </DashboardShell>
  );
}

const inp =
  "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {children}
    </label>
  );
}
